import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Plus, Trash2, UserRound } from "lucide-react";

const empty = { name: "", phone: "", email: "", address: "", notes: "" };

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();

  const load = () => api.get("/clients").then((r) => setClients(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/clients", form);
      toast.success("Client added.");
      setForm(empty);
      setOpen(false);
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this client?",
      description: "This permanently removes the client from your list.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/clients/${id}`);
    toast.success("Deleted.");
    load();
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="clients-page">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Clients</h1>
          <p className="text-slate-500 mt-1">Save customers so you don't retype info every time.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-client-button"><Plus className="h-4 w-4 mr-2" />Add client</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New client</DialogTitle>
              <DialogDescription>Save customer details so you don't have to retype them on every quote.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone-input" /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" /></div>
              </div>
              <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-address-input" /></div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="client-notes-input" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-client-button">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">Loading…</Card>
      ) : clients.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-slate-300">
          <UserRound className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No clients yet. Add one to speed up new quotes.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {clients.map((c) => (
            <Card key={c.id} className="p-5 border-slate-200" data-testid={`client-card-${c.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <div className="text-sm text-slate-500">{c.phone} {c.email && `· ${c.email}`}</div>
                  <div className="text-sm text-slate-500 mt-1">{c.address}</div>
                  {c.notes && <div className="text-xs text-slate-500 mt-2 italic">{c.notes}</div>}
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`delete-client-${c.id}`}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
